/**
 * AccountCard — the card shell every My Account section uses.
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ [icon] Title                        [action] │   header (action = the Edit button)
 *   │        subtitle                              │
 *   ├──────────────────────────────────────────────┤
 *   │ body: read-only DetailRows, or the form      │
 *   ├──────────────────────────────────────────────┤
 *   │                       [Cancel]  [Save]       │   footer — only while editing
 *   └──────────────────────────────────────────────┘
 *
 * Read-first: a section shows its values as text and only turns into inputs when the
 * user presses Edit, with an explicit Save / Cancel. Passing `onSubmit` makes the
 * card itself the <form>, so Enter submits and the footer's primary button is a real
 * submit button.
 */
import { Box, Card, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';

export const ACCENT = '#4f46e5';

/** A soft tinted tile with an outline icon — quieter than a solid colour block. */
export const IconTile: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 36,
}) => (
  <Box
    aria-hidden
    sx={{
      width: size,
      height: size,
      borderRadius: '10px',
      bgcolor: alpha(ACCENT, 0.08),
      color: ACCENT,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

interface AccountCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: React.ReactNode;
  /** Right side of the header — normally the Edit button. */
  action?: React.ReactNode;
  /** Shown under the body in a tinted bar — normally Cancel / Save while editing. */
  footer?: React.ReactNode;
  /** When set the card renders as a <form> that calls this on submit. */
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  /** Escape cancels an edit. */
  onEscape?: () => void;
  children: React.ReactNode;
}

const AccountCard: React.FC<AccountCardProps> = ({
  icon,
  title,
  subtitle,
  action,
  footer,
  onSubmit,
  onEscape,
  children,
}) => {
  const content = (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: { xs: 2, sm: 3 },
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <IconTile>{icon}</IconTile>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{ fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.35, color: 'text.primary' }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', lineHeight: 1.45 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>{children}</Box>
      {footer && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
            px: { xs: 2, sm: 3 },
            py: 1.5,
            bgcolor: '#f8fafc',
            borderTop: '1px solid',
            borderColor: 'divider',
            borderRadius: '0 0 12px 12px',
          }}
        >
          {footer}
        </Box>
      )}
    </>
  );
  return (
    <Card sx={{ overflow: 'visible' }}>
      {onSubmit ? (
        <Box
          component="form"
          noValidate
          onSubmit={onSubmit}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Escape' && onEscape) onEscape();
          }}
        >
          {content}
        </Box>
      ) : (
        content
      )}
    </Card>
  );
};

/** One read-only "label  value" line. Empty values render a quiet dash, not a blank. */
export const DetailRow: React.FC<{
  label: string;
  children?: React.ReactNode;
  hint?: React.ReactNode;
}> = ({ label, children, hint }) => {
  const empty = children === undefined || children === null || children === '';
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '170px 1fr' },
        columnGap: 3,
        rowGap: 0.25,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:first-of-type': { pt: 0 },
        '&:last-of-type': { borderBottom: 0, pb: 0 },
      }}
    >
      <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontWeight: 500 }}>
        {label}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="div"
          sx={{
            fontSize: '0.875rem',
            color: empty ? 'text.disabled' : 'text.primary',
            wordBreak: 'break-word',
          }}
        >
          {empty ? '—' : children}
        </Typography>
        {hint && (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default AccountCard;
